import { useState } from "react";
import {
    Alert,
    Button,
    FormCheckbox,
    Input,
    Select,
} from "@jhool-io/fe-components";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQueryClient } from "@tanstack/react-query";
import { IProvider } from "../../types/providers.types";
import { useFetchActiveProviders } from "../../hooks/providers.queries";
import {
    NoteCategory,
    NoteCategoryLabels,
    NoteTypes,
} from "../../../notes/types/notes.types";
import { useOverrideProvidersSupervisor } from "../../hooks/providers.mutations";
import useToast from "../../../../hooks/useToast";
import {
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { useFetchInsuranceProviders } from "../../../clients/hooks/clients.queries";
import PlusIcon from "../../../../components/Icons/Plus";
import TrashIcon from "../../../../components/Icons/Trash";

interface OverrideSupervisorFormProps {
    onFormSubmit: () => void;
    overrideType: "insurance" | "category";
    providerDetails?: IProvider;
}

type Options = {
    label: string;
    value: string;
};

interface OverrideRow {
    supervisor_id: string | null;
    note_category: string;
    insurance_provider_ids: string[] | null;
    billing_only: boolean;
}

interface FormValues {
    therapyGeneral: {
        supervisor_id: string | null;
        billing_only: boolean;
    };
    testingGeneral: {
        supervisor_id: string | null;
        billing_only: boolean;
    };
    insuranceOverrides: OverrideRow[];
}

const schema = yup.object().shape({
    therapyGeneral: yup.object().shape({
        supervisor_id: yup.string().nullable(),
        billing_only: yup.boolean().required(),
    }),
    testingGeneral: yup.object().shape({
        supervisor_id: yup.string().nullable(),
        billing_only: yup.boolean().required(),
    }),
    insuranceOverrides: yup.array().of(
        yup.object().shape({
            supervisor_id: yup
                .string()
                .nullable()
                .required("Select a supervisor"),
            note_category: yup.string().required("Select a note category"),
            insurance_provider_ids: yup
                .array()
                .of(yup.string())
                .min(1, "Select at least one insurance")
                .required("Select at least one insurance"),
            billing_only: yup.boolean().required(),
        })
    ),
});

const noteCategoryOptions = [
    {
        label: makeStringFirstLetterCapital(
            NoteCategoryLabels[NoteCategory.THERAPY]
        ),
        value: NoteCategory.THERAPY,
    },
    {
        label: makeStringFirstLetterCapital(
            NoteCategoryLabels[NoteCategory.TESTING]
        ),
        value: NoteCategory.TESTING,
    },
];

export default function OverrideSupervisorForm({
    onFormSubmit,
    overrideType,
    providerDetails,
}: OverrideSupervisorFormProps) {
    const [providerName, setProviderName] = useState("");

    const { data, error, isLoading, isSuccess } =
        useFetchActiveProviders(providerName);

    const { data: insuranceData, isLoading: insuranceLoading } =
        useFetchInsuranceProviders();

    const supervisorsForSelect = data?.data.map((supervisor) => ({
        label: <div className="capitalize">{supervisor.provider_name}</div>,
        value: supervisor.provider_id,
    }));

    const insuranceProvidersForSelect =
        insuranceData?.data?.map((ip) => ({
            label: ip.name,
            value: ip.insurance_provider_id,
        })) ?? [];

    const overrideProviderSupervisor = useOverrideProvidersSupervisor(
        providerDetails?.provider_id as string
    );

    const queryClient = useQueryClient();
    const { toast } = useToast();

    const getGeneralOverrideForCategory = (category: NoteCategory) => {
        const override = providerDetails?.supervisor_overrides?.find(
            (val) =>
                val.note_category === category &&
                !(
                    (val?.insurance_provider_ids?.length ?? 0) > 0 ||
                    (val?.insurance_providers?.length ?? 0) > 0
                )
        );
        return {
            supervisor_id: override?.supervisor_id ?? null,
            billing_only: override?.billing_only ?? false,
        };
    };

    const existingInsuranceOverrides =
        providerDetails?.supervisor_overrides?.filter(
            (val) =>
                (val?.insurance_provider_ids?.length ?? 0) > 0 ||
                (val?.insurance_providers?.length ?? 0) > 0
        ) ?? [];

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: yupResolver(schema) as never,
        values: isSuccess
            ? {
                  therapyGeneral: getGeneralOverrideForCategory(
                      NoteCategory.THERAPY
                  ),
                  testingGeneral: getGeneralOverrideForCategory(
                      NoteCategory.TESTING
                  ),
                  insuranceOverrides: existingInsuranceOverrides.map(
                      (override) => ({
                          supervisor_id: override.supervisor_id,
                          note_category: override.note_category,
                          insurance_provider_ids:
                              override.insurance_provider_ids ?? [],
                          billing_only: override.billing_only ?? false,
                      })
                  ),
              }
            : undefined,
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "insuranceOverrides",
    });

    const saveOverrides = (payload: FormValues) => {
        const dataToSend = [
            {
                supervisor_id: payload.therapyGeneral.supervisor_id ?? null,
                note_category: NoteCategory.THERAPY,
                insurance_provider_ids: null,
                billing_only: payload.therapyGeneral.billing_only,
            },
            {
                supervisor_id: payload.testingGeneral.supervisor_id ?? null,
                note_category: NoteCategory.TESTING,
                insurance_provider_ids: null,
                billing_only: payload.testingGeneral.billing_only,
            },
            ...payload.insuranceOverrides.map((override) => ({
                supervisor_id: override.supervisor_id,
                note_category: override.note_category,
                insurance_provider_ids: override.insurance_provider_ids,
                billing_only: override.billing_only,
            })),
        ];

        overrideProviderSupervisor.mutate(
            {
                overrides: dataToSend,
            },
            {
                onSuccess: (res) => {
                    queryClient.invalidateQueries({
                        queryKey: ["providers"],
                    });
                    toast({
                        mode: "success",
                        message:
                            res.message || "Supervisors updated successfully",
                    });
                    onFormSubmit();
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update supervisors at this time",
                    });
                },
            }
        );
    };

    const displayNoteType = (noteType: NoteTypes) =>
        makeStringFirstLetterCapital(removeEnumUnderscore(noteType));

    const getSupervisorName = (supervisorId: string | null) => {
        if (!supervisorId) {
            return null;
        }
        const selectedSupervisor = data?.data.find(
            (provider) => provider.provider_id === supervisorId
        );
        if (selectedSupervisor) {
            return selectedSupervisor.provider_name;
        }
        const existingSupervisor = providerDetails?.supervisor_overrides?.find(
            (override) => override.supervisor_id === supervisorId
        )?.supervisor;
        if (!existingSupervisor) {
            return "the selected supervisor";
        }
        return [
            existingSupervisor.user.first_name,
            existingSupervisor.user.last_name,
        ]
            .filter(Boolean)
            .join(" ");
    };

    const getCategoryLabel = (category: string) =>
        category === NoteCategory.TESTING ? "Psych testing" : "Therapy";

    const getInsuranceNames = (insuranceProviderIds: string[] | null) =>
        insuranceProvidersForSelect
            .filter((insurance) =>
                insuranceProviderIds?.includes(insurance.value)
            )
            .map((insurance) => insurance.label);

    const getCategoryDefaultSummary = (
        category: string,
        supervisorId: string | null,
        billingOnly: boolean
    ) => {
        const supervisor = getSupervisorName(supervisorId);
        if (!supervisor) {
            return null;
        }
        if (billingOnly) {
            return `${getCategoryLabel(
                category
            )} claims will be billed under ${supervisor}, except where an
            insurance exception applies. Note review will continue through the
            usual supervision workflow.`;
        }
        return `${supervisor} will supervise ${getCategoryLabel(
            category
        )} notes and be used for billing, except where an insurance exception
        applies.`;
    };

    const categoryDefaultItems = (
        <div className="pt-4">
            <div className="mb-4">
                <h3 className="text-sm font-semibold mb-1">
                    Default supervisors by note category
                </h3>
                <p className="text-xs text-gray">
                    Use this when Therapy and Psych notes require different
                    supervisors. These settings apply across all insurance
                    unless an insurance exception is configured.
                </p>
            </div>
            <div className="border rounded-r8 p-4 mb-4 bg-card">
                <h4 className="text-xs font-semibold mb-3">Therapy notes</h4>
                <div className="fg fg-space-between two flex">
                    <Input
                        label="Note category"
                        infoText={`Therapy notes include ${displayNoteType(
                            NoteTypes.INDIVIDUAL_INTAKE_NOTE
                        )}, ${displayNoteType(
                            NoteTypes.INDIVIDUAL_SOAP_NOTE
                        )}, ${displayNoteType(NoteTypes.SAFETY_PLAN)}, etc.`}
                        disabled
                        value={makeStringFirstLetterCapital(
                            NoteCategoryLabels[NoteCategory.THERAPY]
                        )}
                    />
                    <Controller
                        name="therapyGeneral.supervisor_id"
                        control={control}
                        render={({ field }) => (
                            <Select
                                label="Supervisor"
                                options={
                                    data || !error || !isLoading
                                        ? supervisorsForSelect
                                        : []
                                }
                                isDisabled={Boolean(error)}
                                onInputChange={(value) =>
                                    setProviderName(value)
                                }
                                filterOption={() => true}
                                isSearchable
                                placeholder={
                                    (!data && isLoading && "Loading...") ||
                                    (error &&
                                        !isLoading &&
                                        "Error loading providers") ||
                                    (data &&
                                        !isLoading &&
                                        "Select a supervisor")
                                }
                                isClearable
                                onChange={(val) => {
                                    const newVal = val
                                        ? (val as Options).value
                                        : null;
                                    field.onChange(newVal);
                                    if (!newVal) {
                                        setValue(
                                            "therapyGeneral.billing_only",
                                            false
                                        );
                                    }
                                }}
                                noOptionsMessage={() =>
                                    isLoading ? "Loading..." : "No options"
                                }
                                value={
                                    field.value
                                        ? supervisorsForSelect?.find(
                                              (option) =>
                                                  option.value === field.value
                                          )
                                        : null
                                }
                                isLongListInDialog
                            />
                        )}
                    />
                </div>
                {watch("therapyGeneral.supervisor_id") && (
                    <>
                        <div className="mb-3">
                            <Controller
                                name="therapyGeneral.billing_only"
                                control={control}
                                render={({ field }) => (
                                    <FormCheckbox
                                        label="Use this supervisor for claim billing only"
                                        labelClass="!text-textSec"
                                        checked={field.value}
                                        onChange={(event) =>
                                            field.onChange(event.target.checked)
                                        }
                                    />
                                )}
                            />
                        </div>
                        <Alert
                            type="info"
                            description={getCategoryDefaultSummary(
                                NoteCategory.THERAPY,
                                watch("therapyGeneral.supervisor_id"),
                                watch("therapyGeneral.billing_only")
                            )}
                        />
                    </>
                )}
            </div>
            <div className="border rounded-r8 p-4 bg-card">
                <h4 className="text-xs font-semibold mb-3">
                    Psych testing notes
                </h4>
                <div className="fg fg-space-between two flex">
                    <Input
                        label="Note category"
                        infoText={`Psych notes include ${displayNoteType(
                            NoteTypes.FEEDBACK_PSYCH
                        )}, ${displayNoteType(
                            NoteTypes.SCORING_BY_PSYCH
                        )}, ${displayNoteType(
                            NoteTypes.SCORING_BY_TECH_PSYCH
                        )}, etc.`}
                        disabled
                        value={makeStringFirstLetterCapital(
                            NoteCategoryLabels[NoteCategory.TESTING]
                        )}
                    />
                    <Controller
                        name="testingGeneral.supervisor_id"
                        control={control}
                        render={({ field }) => (
                            <Select
                                label="Supervisor"
                                options={
                                    data || !error || !isLoading
                                        ? supervisorsForSelect
                                        : []
                                }
                                isDisabled={Boolean(error)}
                                onInputChange={(value) =>
                                    setProviderName(value)
                                }
                                filterOption={() => true}
                                isSearchable
                                placeholder={
                                    (!data && isLoading && "Loading...") ||
                                    (error &&
                                        !isLoading &&
                                        "Error loading providers") ||
                                    (data &&
                                        !isLoading &&
                                        "Select a supervisor")
                                }
                                isClearable
                                onChange={(val) => {
                                    const newVal = val
                                        ? (val as Options).value
                                        : null;
                                    field.onChange(newVal);
                                    if (!newVal) {
                                        setValue(
                                            "testingGeneral.billing_only",
                                            false
                                        );
                                    }
                                }}
                                noOptionsMessage={() =>
                                    isLoading ? "Loading..." : "No options"
                                }
                                value={
                                    field.value
                                        ? supervisorsForSelect?.find(
                                              (option) =>
                                                  option.value === field.value
                                          )
                                        : null
                                }
                                isLongListInDialog
                            />
                        )}
                    />
                </div>
                {watch("testingGeneral.supervisor_id") && (
                    <>
                        <div className="mb-3">
                            <Controller
                                name="testingGeneral.billing_only"
                                control={control}
                                render={({ field }) => (
                                    <FormCheckbox
                                        label="Use this supervisor for claim billing only"
                                        labelClass="!text-textSec"
                                        checked={field.value}
                                        onChange={(event) =>
                                            field.onChange(event.target.checked)
                                        }
                                    />
                                )}
                            />
                        </div>
                        <Alert
                            type="info"
                            description={getCategoryDefaultSummary(
                                NoteCategory.TESTING,
                                watch("testingGeneral.supervisor_id"),
                                watch("testingGeneral.billing_only")
                            )}
                        />
                    </>
                )}
            </div>
        </div>
    );

    const insuranceExceptionItems = (
        <div className="pt-4">
            <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                    <h3 className="text-sm font-semibold mb-1">
                        Supervisor exceptions by insurance
                    </h3>
                    <p className="text-xs text-gray">
                        Use this when a specific insurance requires a different
                        supervisor. Exceptions only affect the selected
                        insurance and take priority over category defaults.
                    </p>
                </div>
                <Button
                    size="small"
                    type="button"
                    className="shrink-0 whitespace-nowrap"
                    iconLeft={<PlusIcon />}
                    onClick={() =>
                        append({
                            supervisor_id: null,
                            note_category: NoteCategory.THERAPY,
                            insurance_provider_ids: [],
                            billing_only: false,
                        })
                    }
                >
                    Add insurance exception
                </Button>
            </div>
            {fields.length === 0 && (
                <div className="border rounded-r8 p-4 bg-card">
                    <p className="text-xs text-gray">
                        No insurance exceptions have been added. Category
                        defaults or the provider&apos;s assigned supervisor will
                        be used.
                    </p>
                </div>
            )}
            {fields.map((field, index) => {
                const supervisorId = watch(
                    `insuranceOverrides.${index}.supervisor_id`
                );
                const insuranceIds =
                    watch(
                        `insuranceOverrides.${index}.insurance_provider_ids`
                    ) ?? [];
                const category = watch(
                    `insuranceOverrides.${index}.note_category`
                );
                const billingOnly = watch(
                    `insuranceOverrides.${index}.billing_only`
                );
                const supervisor = getSupervisorName(supervisorId);
                const insuranceNames = getInsuranceNames(insuranceIds);
                const exceptionName =
                    insuranceNames.length > 0
                        ? `${insuranceNames.join(", ")} · ${getCategoryLabel(
                              category
                          )}`
                        : `Insurance exception ${index + 1}`;
                return (
                    <div
                        key={field.id}
                        className="border rounded-r8 p-4 mb-4 bg-card"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-semibold">
                                {exceptionName}
                            </span>
                            <Button
                                variant="danger"
                                size="small"
                                type="button"
                                className="shrink-0 whitespace-nowrap"
                                iconLeft={
                                    <TrashIcon
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                    />
                                }
                                aria-label={`Remove ${exceptionName}`}
                                onClick={() => remove(index)}
                            >
                                Remove exception
                            </Button>
                        </div>
                        <Controller
                            name={`insuranceOverrides.${index}.insurance_provider_ids`}
                            control={control}
                            render={({ field: insuranceField }) => (
                                <Select
                                    label="Insurance providers"
                                    options={insuranceProvidersForSelect}
                                    isMulti
                                    isSearchable
                                    placeholder={
                                        insuranceLoading
                                            ? "Loading..."
                                            : "Select insurance providers"
                                    }
                                    onChange={(val) => {
                                        const selected = val
                                            ? (val as Options[]).map(
                                                  (option) => option.value
                                              )
                                            : [];
                                        insuranceField.onChange(selected);
                                    }}
                                    value={insuranceProvidersForSelect.filter(
                                        (option) =>
                                            (
                                                insuranceField.value as
                                                    | string[]
                                                    | null
                                            )?.includes(option.value)
                                    )}
                                    noOptionsMessage={() =>
                                        insuranceLoading
                                            ? "Loading..."
                                            : "No options"
                                    }
                                    hasError={
                                        !!errors.insuranceOverrides?.[index]
                                            ?.insurance_provider_ids
                                    }
                                    errorText={
                                        errors.insuranceOverrides?.[index]
                                            ?.insurance_provider_ids?.message
                                    }
                                    isLongListInDialog
                                />
                            )}
                        />
                        <div className="fg fg-space-between two flex mt-3">
                            <Controller
                                name={`insuranceOverrides.${index}.note_category`}
                                control={control}
                                render={({ field: categoryField }) => (
                                    <Select
                                        label="Note category"
                                        options={noteCategoryOptions}
                                        onChange={(val) => {
                                            categoryField.onChange(
                                                val
                                                    ? (val as Options).value
                                                    : ""
                                            );
                                        }}
                                        value={noteCategoryOptions.find(
                                            (option) =>
                                                option.value ===
                                                categoryField.value
                                        )}
                                        hasError={
                                            !!errors.insuranceOverrides?.[index]
                                                ?.note_category
                                        }
                                        errorText={
                                            errors.insuranceOverrides?.[index]
                                                ?.note_category?.message
                                        }
                                    />
                                )}
                            />
                            <Controller
                                name={`insuranceOverrides.${index}.supervisor_id`}
                                control={control}
                                render={({ field: supervisorField }) => (
                                    <Select
                                        label="Supervisor"
                                        options={
                                            data || !error || !isLoading
                                                ? supervisorsForSelect
                                                : []
                                        }
                                        isDisabled={Boolean(error)}
                                        onInputChange={(value) =>
                                            setProviderName(value)
                                        }
                                        filterOption={() => true}
                                        isSearchable
                                        placeholder={
                                            (!data &&
                                                isLoading &&
                                                "Loading...") ||
                                            (error &&
                                                !isLoading &&
                                                "Error loading providers") ||
                                            (data &&
                                                !isLoading &&
                                                "Select a supervisor")
                                        }
                                        isClearable
                                        onChange={(val) => {
                                            const newVal = val
                                                ? (val as Options).value
                                                : null;
                                            supervisorField.onChange(newVal);
                                            if (!newVal) {
                                                setValue(
                                                    `insuranceOverrides.${index}.billing_only`,
                                                    false
                                                );
                                            }
                                        }}
                                        noOptionsMessage={() =>
                                            isLoading
                                                ? "Loading..."
                                                : "No options"
                                        }
                                        hasError={
                                            !!errors.insuranceOverrides?.[index]
                                                ?.supervisor_id
                                        }
                                        errorText={
                                            errors.insuranceOverrides?.[index]
                                                ?.supervisor_id?.message
                                        }
                                        value={
                                            supervisorField.value
                                                ? supervisorsForSelect?.find(
                                                      (option) =>
                                                          option.value ===
                                                          supervisorField.value
                                                  )
                                                : null
                                        }
                                        isLongListInDialog
                                    />
                                )}
                            />
                        </div>
                        {supervisorId && (
                            <>
                                <div className="mt-3 mb-3">
                                    <Controller
                                        name={`insuranceOverrides.${index}.billing_only`}
                                        control={control}
                                        render={({
                                            field: billingOnlyField,
                                        }) => (
                                            <FormCheckbox
                                                label="Use this supervisor for claim billing only"
                                                labelClass="!text-textSec"
                                                checked={billingOnlyField.value}
                                                onChange={(event) =>
                                                    billingOnlyField.onChange(
                                                        event.target.checked
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                </div>
                                {insuranceNames.length > 0 && supervisor && (
                                    <Alert
                                        type="info"
                                        description={
                                            billingOnly
                                                ? `${insuranceNames.join(
                                                      ", "
                                                  )} ${getCategoryLabel(
                                                      category
                                                  )} claims will be billed under
                                                  ${supervisor}. Other insurance
                                                  will continue using the
                                                  category default.`
                                                : `${supervisor} will supervise
                                                  ${getCategoryLabel(
                                                      category
                                                  )} notes and be used for
                                                  billing for ${insuranceNames.join(
                                                      ", "
                                                  )}. Other insurance will
                                                  continue using the category
                                                  default.`
                                        }
                                    />
                                )}
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );

    return (
        <form id="override-supervisor" onSubmit={handleSubmit(saveOverrides)}>
            <Alert
                type="info"
                description={
                    <div>
                        {overrideType === "insurance" ? (
                            <>
                                <p className="mb-1">
                                    Insurance exceptions apply only to the
                                    selected insurance and take priority over
                                    category defaults.
                                </p>
                                <p>
                                    If no exception applies, the category
                                    default or{" "}
                                    {providerDetails?.supervisor_name ??
                                        "the provider's assigned supervisor"}{" "}
                                    is used.
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="mb-1">
                                    Category defaults apply by note category
                                    across all insurance. An insurance exception
                                    takes priority for the insurance it covers.
                                </p>
                                <p>
                                    If no category default applies,{" "}
                                    {providerDetails?.supervisor_name ??
                                        "the provider's assigned supervisor"}{" "}
                                    is used.
                                </p>
                            </>
                        )}
                    </div>
                }
                classNames="mb-4"
            />
            {overrideType === "insurance"
                ? insuranceExceptionItems
                : categoryDefaultItems}
        </form>
    );
}
